﻿import type { GroupRowInfo } from '../types/modules/expand-collapse-buttons';
import { Nullable } from '../types/utils/common';

function selectButtonAggregation_onSelect({ sender, indices: [selectedLink] }: kendo.ui.ButtonGroupSelectEvent) {
    const actionMap = {
        collapseFirstLevel: 0,
        collapsedAll: 1,
        expandFirstLevel: 2,
        expandAll: 3
    };

    const gridId = sender.element.data('for-grid') || document.querySelector('.k-grid')?.id || '';
    const gridInstance = window[gridId];

    if (gridInstance instanceof kendo.ui.Grid) {
        switch (selectedLink) {
            case actionMap.collapseFirstLevel:
                window.expandCollapseButtons_btCollapseFirstLevel_onClick(gridInstance);
                break;
            case actionMap.collapsedAll:
                window.expandCollapseButtons_btCollapse_onClick(gridInstance);
                break;
            case actionMap.expandFirstLevel:
                window.expandCollapseButtons_btExpandFirstLevel_onClick(gridInstance);
                break;
            case actionMap.expandAll:
                window.expandCollapseButtons_btExpand_onClick(gridInstance);
        }
    }
}

/**
 * Собирает все группировочные строки грида:
 * возвращает массив с элементом строки, уровнем вложенности и флагом раскрытости.
 */
function getGroupRows(grid: kendo.ui.Grid): GroupRowInfo[] {
    const result: GroupRowInfo[] = [];

    grid.element.find('tr.k-grouping-row').each((_, row) => {
        const $row = $(row);
        const level = $row.find('td:not([hidden])').length;
        const $toggle = $row.find('[aria-expanded]');
        const isExpanded = $toggle.length
                           ? $toggle.attr('aria-expanded') === 'true'
                           : true;

        result.push({ row: row as HTMLTableRowElement, level, isExpanded });
    });

    return result;
}

/**
 * Раскрывает все свернутые группы в гриде.
 */
function expandCollapseButtons_btExpand_onClick(grid: kendo.ui.Grid) {
    const groupRows = getGroupRows(grid);

    for (const { row, isExpanded } of groupRows) {
        if (!isExpanded) {
            grid.expandGroup(row);
        }
    }
}

/**
 * Сворачивает все раскрытые группы в гриде.
 */
function expandCollapseButtons_btCollapse_onClick(grid: kendo.ui.Grid) {
    const groupRows = getGroupRows(grid);

    for (const { row, isExpanded } of groupRows) {
        if (isExpanded) {
            grid.collapseGroup(row);
        }
    }
}

/**
 * Раскрывает только первый из свернутых уровней группировки.
 */
function expandCollapseButtons_btExpandFirstLevel_onClick(grid: kendo.ui.Grid) {
    const groupRows = getGroupRows(grid);
    const minCollapsedLevel = getMinCollapsedLevel(groupRows);

    if (minCollapsedLevel !== null) {
        for (const { row, level, isExpanded } of groupRows) {
            if (level === minCollapsedLevel && !isExpanded) {
                grid.expandGroup(row);
            }
        }
    }
}

/**
 * Сворачивает один раскрытый уровень группировки.
 */
function expandCollapseButtons_btCollapseFirstLevel_onClick(grid: kendo.ui.Grid) {
    const groupRows = getGroupRows(grid);

    if (groupRows.length) {
        const maxExpendedLevel = getMaxExpendedLevel(groupRows);
        const maxValue = getValueMaxLevelFromGrid(groupRows);

        if (maxExpendedLevel <= maxValue) {
            for (const { row, level } of groupRows) {
                if (level === maxExpendedLevel || level === maxExpendedLevel + 1) {
                    grid.collapseGroup(row);
                }
            }
        }
    }
}

/**
 * Возвращает максимальный уровень вложенности среди всех групп.
 */
function getValueMaxLevelFromGrid(groupRows: GroupRowInfo[]) {
    let max = 0;
    for (let i = 0; i < groupRows.length; i++) {
        const level = groupRows[i].level;
        if (level > max) {
            max = level;
        }
    }
    return max;
}

/**
 * Возвращает максимальный уровень вложенности среди раскрытых групп.
 */
function getMaxExpendedLevel(groupRows: GroupRowInfo[]) {
    let max = 1;

    for (let i = 0; i < groupRows.length; i++) {
        const { level, isExpanded } = groupRows[i];

        if (isExpanded && level > max) {
            max = level;
        }
    }

    return max;
}

/**
 * Возвращает минимальный уровень среди свернутых групп или null, если все уровни раскрыты.
 */
function getMinCollapsedLevel(groupRows: GroupRowInfo[]) {
    let minLevel: Nullable<number> = null;

    for (const { level, isExpanded } of groupRows) {
        if (!isExpanded) {
            if (minLevel === null || level < minLevel) {
                minLevel = level;
            }
        }
    }
    return minLevel;
}

window.selectButtonAggregation_onSelect = selectButtonAggregation_onSelect;
window.expandCollapseButtons_btExpand_onClick = expandCollapseButtons_btExpand_onClick;
window.expandCollapseButtons_btCollapse_onClick = expandCollapseButtons_btCollapse_onClick;
window.expandCollapseButtons_btExpandFirstLevel_onClick = expandCollapseButtons_btExpandFirstLevel_onClick;
window.expandCollapseButtons_btCollapseFirstLevel_onClick = expandCollapseButtons_btCollapseFirstLevel_onClick;
window.getValueMaxLevelFromGrid = getValueMaxLevelFromGrid;
window.getMaxExpendedLevel = getMaxExpendedLevel;
